// react-native-maps has no web build, so it used to be aliased to the null
// `noop` shim: every <MapView> (OrdersMapView, Location, TLAddress) rendered a
// blank grey hole on web. This shim renders a real map with Leaflet +
// OpenStreetMap tiles (no API key: the app's Google key is an Android key and
// cannot be used from a browser). It covers the subset of the react-native-maps
// API the app uses: MapView (initialRegion / controlled region /
// onRegionChangeComplete / onPress / zoom+scroll flags), Marker (custom React
// children, onPress), Polygon and Circle. Aliased via react-native-maps$.
const React = require("react");
const { createPortal } = require("react-dom");
const L = require("leaflet");
const View = require("react-native-web/dist/exports/View").default;
const leafletCss = require("leaflet/dist/leaflet.css");

const {
  createElement: h,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} = React;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === "undefined") return;
  cssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-leaflet", "1.9.4");
  style.textContent = leafletCss;
  document.head.appendChild(style);
}

const MapContext = createContext(null);

const toLatLng = (c) => [Number(c.latitude), Number(c.longitude)];
// Backend can send placeholder points (delivery-tasks-map-v2 returns
// cp_polygon_coords: [{}] for a CP with no polygon); native skips them, Leaflet
// throws "Invalid LatLng" and would take the whole screen down.
const isValid = (c) =>
  !!c &&
  Number.isFinite(Number(c.latitude)) &&
  Number.isFinite(Number(c.longitude));
const deltaToZoom = (delta) =>
  Math.round(Math.log2(360 / (Number(delta) || 0.04)));

function regionOf(map) {
  const b = map.getBounds();
  const c = map.getCenter();
  return {
    latitude: c.lat,
    longitude: c.lng,
    latitudeDelta: b.getNorth() - b.getSouth(),
    longitudeDelta: b.getEast() - b.getWest(),
  };
}

function MapView(props) {
  const {
    style,
    children,
    initialRegion,
    region,
    onRegionChangeComplete,
    onPress,
    minZoomLevel = 2,
    maxZoomLevel = 19,
    zoomEnabled = true,
    scrollEnabled = true,
    zoomControlEnabled = true,
  } = props;
  const divRef = useRef(null);
  const [map, setMap] = useState(null);
  const handlers = useRef({});
  handlers.current = { onRegionChangeComplete, onPress };

  useEffect(() => {
    injectCss();
    const start = region || initialRegion;
    const m = L.map(divRef.current, {
      center: start ? toLatLng(start) : [20.59, 78.96],
      zoom: start ? deltaToZoom(start.latitudeDelta) : 5,
      minZoom: minZoomLevel,
      maxZoom: Math.min(maxZoomLevel, 19),
      zoomControl: false, // added bottom-right below, where native puts it
      dragging: scrollEnabled,
      scrollWheelZoom: zoomEnabled,
      doubleClickZoom: zoomEnabled,
      touchZoom: zoomEnabled,
      boxZoom: zoomEnabled,
    });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(m);
    if (zoomEnabled && zoomControlEnabled) {
      L.control.zoom({ position: "bottomright" }).addTo(m);
    }
    m.on("moveend", () => {
      const cb = handlers.current.onRegionChangeComplete;
      if (cb) cb(regionOf(m), { isGesture: false });
    });
    m.on("click", (e) => {
      const cb = handlers.current.onPress;
      if (cb) {
        cb({
          nativeEvent: {
            coordinate: { latitude: e.latlng.lat, longitude: e.latlng.lng },
          },
        });
      }
    });
    // The container is laid out by RNW after mount; re-measure on resize.
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => m.invalidateSize())
        : null;
    if (ro) ro.observe(divRef.current);
    setMap(m);
    return () => {
      if (ro) ro.disconnect();
      m.remove();
    };
    // Created once per mount, like the native view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Controlled `region`: move only when it differs from where the map already
  // is, so region -> moveend -> setRegion -> region does not loop.
  useEffect(() => {
    if (!map || !region) return;
    const c = map.getCenter();
    const zoom = deltaToZoom(region.latitudeDelta);
    const moved =
      Math.abs(c.lat - region.latitude) > 1e-6 ||
      Math.abs(c.lng - region.longitude) > 1e-6;
    if (moved || zoom !== map.getZoom()) map.setView(toLatLng(region), zoom);
  }, [map, region]);

  return h(
    View,
    { style },
    h("div", {
      ref: divRef,
      // zIndex 0 = own stacking context, so Leaflet's pane z-indexes (400+)
      // stay under the app's absolutely-positioned overlays.
      style: { position: "relative", zIndex: 0, width: "100%", height: "100%" },
    }),
    map ? h(MapContext.Provider, { value: map }, children) : null,
  );
}

function Marker({ coordinate, onPress, children, zIndex }) {
  const map = useContext(MapContext);
  const [el] = useState(() => document.createElement("div"));
  const markerRef = useRef(null);
  const pressRef = useRef(onPress);
  pressRef.current = onPress;

  useEffect(() => {
    if (!map || !isValid(coordinate)) return undefined;
    // Anchor the custom view bottom-centre on the coordinate, like native.
    el.style.cssText =
      "position:absolute;transform:translate(-50%,-100%);white-space:nowrap;cursor:pointer";
    const m = L.marker(toLatLng(coordinate), {
      icon: L.divIcon({ html: el, className: "", iconSize: [0, 0] }),
      zIndexOffset: zIndex || 0,
    }).addTo(map);
    m.on("click", () => pressRef.current && pressRef.current());
    markerRef.current = m;
    return () => m.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, el]);

  useEffect(() => {
    if (markerRef.current && isValid(coordinate))
      markerRef.current.setLatLng(toLatLng(coordinate));
  }, [coordinate.latitude, coordinate.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  const content =
    children ||
    h("div", {
      style: {
        width: 14,
        height: 14,
        borderRadius: 7,
        background: "#e53935",
        border: "2px solid #fff",
      },
    });
  return createPortal(content, el);
}

function useShape(create, deps) {
  const map = useContext(MapContext);
  useEffect(() => {
    if (!map) return undefined;
    const layer = create();
    if (!layer) return undefined;
    layer.addTo(map);
    return () => layer.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ...deps]);
  return null;
}

function Polygon({ coordinates = [], strokeColor, strokeWidth, fillColor }) {
  const points = (coordinates || []).filter(isValid).map(toLatLng);
  useShape(
    () =>
      points.length < 3
        ? null
        : L.polygon(points, {
            color: strokeColor || "#000",
            weight: strokeWidth == null ? 1 : strokeWidth,
            fillColor: fillColor || "transparent",
            fillOpacity: 1, // alpha comes from the rgba() colour, as on native
            interactive: false,
          }),
    [JSON.stringify(coordinates), strokeColor, strokeWidth, fillColor],
  );
  return null;
}

function Circle({ center, radius, strokeColor, strokeWidth, fillColor }) {
  useShape(
    () =>
      !isValid(center)
        ? null
        : L.circle(toLatLng(center), {
            radius: radius || 0,
            color: strokeColor || "#000",
            weight: strokeWidth == null ? 1 : strokeWidth,
            fillColor: fillColor || "transparent",
            fillOpacity: 1,
            interactive: false,
          }),
    [
      center && center.latitude,
      center && center.longitude,
      radius,
      strokeColor,
      strokeWidth,
      fillColor,
    ],
  );
  return null;
}

module.exports = {
  __esModule: true,
  default: MapView,
  MapView,
  Marker,
  Polygon,
  Circle,
  PROVIDER_GOOGLE: "google",
  PROVIDER_DEFAULT: null,
};
